import { useCallback, useState } from "react";
import { type AuthContextProps, useAuth } from "react-oidc-context";
import { PAGE_PATHS } from "@/config/urls";
import { useLogin } from "@/hooks/useLogin";
import { useOidcTokenRefresh } from "@/hooks/useOidcTokenRefresh";
import { saveOriginalUrl } from "@/lib/TokenManager";

export const useAuthFlow = () => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const auth = useAuth();
  const { refreshAccessToken } = useOidcTokenRefresh();
  const { login } = useLogin();

  /**
   * HumHubログイン実行（useLoginフックを使用）
   */
  const loginToHumHub = useCallback(
    async (accessToken: string): Promise<boolean> => {
      try {
        const result = await login(accessToken);
        return !!result.auth_token;
      } catch (error) {
        console.error("❌ HumHub login failed:", error);
        return false;
      }
    },
    [login],
  );

  /**
   * OIDC認証開始（コールバックURL検出対応）
   */
  const startOidcAuth = useCallback((auth: AuthContextProps) => {
    // 現在のURLが既にコールバックURLの場合は保存しない
    const currentUrl = window.location.href;
    if (!currentUrl.includes(PAGE_PATHS.CALLBACK)) {
      saveOriginalUrl(currentUrl);
    }

    // OIDC認証リダイレクト
    auth.signinRedirect();
  }, []);

  /**
   * 統合認証フロー実行
   */
  const executeAuthFlow = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    setIsAuthenticating(true);

    try {
      // OIDCトークンが存在する場合はHumHubログインを試行
      if (auth.isAuthenticated && auth.user?.access_token) {
        if (await loginToHumHub(auth.user.access_token)) {
          return { success: true };
        }
      }

      try {
        // OIDCトークンがない場合はトークンをリフレッシュしてHumHubログインを試行
        const refreshedAccessToken = await refreshAccessToken();
        if (refreshedAccessToken && (await loginToHumHub(refreshedAccessToken))) {
          return { success: true };
        }
      } catch (_refreshError) {
        console.log("⚠️ Refresh failed, proceeding to OIDC auth");
      }

      // OIDCトークンのリフレッシュに失敗した場合、OIDC認証開始
      startOidcAuth(auth);
      return { success: false, error: "Redirecting to authentication" };
    } catch (error) {
      console.error("❌ Auth flow error:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    } finally {
      setIsAuthenticating(false);
    }
  }, [
    auth,
    refreshAccessToken,
    loginToHumHub, // OIDCトークンのリフレッシュに失敗した場合、OIDC認証開始
    startOidcAuth,
  ]);

  return {
    executeAuthFlow,
    isAuthenticating,
  };
};
